import RiveComponent, { RiveAlignment } from '../../RiveComponent'
import RenderHTML from '../../ui-components/RenderHTML'
import { PrimaryButton } from '../PrimaryButton'
import { SecondaryButton } from '../SecondaryButton'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { useEffect } from 'react'
import { TerminalStatusContext } from '../../../types/mf-execution.types'

export const Failure = ({
  title,
  caption,
  ctas,
  statusContext
}: {
  title: string
  caption?: string
  ctas: { ctaType: 'primary' | 'secondary'; label: string; onClick: () => void }[]
  statusContext?: TerminalStatusContext
}) => {
  const getScreenName = () => {
    switch (statusContext) {
      case TerminalStatusContext.AUTO_PAY:
        return 'Autopay_setup_failed'
      default:
        return 'Payment failed'
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: getScreenName()
      }
    })
  }, [])

  return (
    <div
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 8px)` }}
      className='flex flex-col h-full w-full relative bg-white px-5 animate-fade-250 pt-[35vh]'
    >
      {/* Animated Cancel Icon */}
      <div className='flex mt-3 h-12 w-12 mt-12'>
        <RiveComponent
          src={`/rives/mf-execution/failed.riv`}
          alignment={RiveAlignment.CENTER_LEFT}
          fallback={<FailureFallback />}
        />
      </div>

      {/* Text */}
      <div className='text-[28px] font-[500] mt-4 text-[#232323] leading-[150%] text-left animate-fade-150'>
        <RenderHTML innerHTMLText={title} />
      </div>

      {caption && (
        <div className='text-[14px] font-[400] text-[#6A6E6E] leading-[160%] text-left mt-3 animate-fade-150'>
          <RenderHTML innerHTMLText={caption} />
        </div>
      )}

      <div
        style={{ bottom: `${getBottomPadding() + 8}px` }}
        className='flex gap-4 w-full justify-center items-end absolute bottom-0 left-0 px-5 z-10'
      >
        {ctas.map((cta) =>
          cta.ctaType === 'primary' ? (
            <PrimaryButton
              key={cta.label}
              onClick={cta.onClick}
              label={cta.label}
              primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
            />
          ) : (
            <SecondaryButton
              key={cta.label}
              onClick={cta.onClick}
              label={cta.label}
              secondaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
            />
          )
        )}
      </div>
    </div>
  )
}

export const FailureFallback = () => {
  return (
    <div className='flex justify-start h-max w-full mt-auto'>
      <CancelIcon />
    </div>
  )
}

export const CancelIcon = () => {
  const cancelIconStyles = {
    container: {
      width: '34px',
      height: '34px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    path: {
      strokeDasharray: '48.08',
      strokeDashoffset: '48.08',
      animation: 'drawCancel 0.8s ease-out 0.2s forwards'
    }
  }

  return (
    <div style={cancelIconStyles.container}>
      <svg width='34' height='34' viewBox='0 0 34 34' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M8 8L26 26M26 8L8 26'
          stroke='#E77464'
          strokeWidth='5'
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
          style={cancelIconStyles.path}
        />
      </svg>
      <style>
        {`
          @keyframes drawCancel {
            0% {
              stroke-dashoffset: 36.77;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>
    </div>
  )
}
